/**
 * Chart API service — fetch stored chart specs by ID.
 */
class ChartService {
  static API_BASE = (() => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    return baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
  })();

  /**
   * Fetch a stored chart specification by ID.
   * @param {string} chartId - UUID of the stored chart spec
   * @param {string} token - Bearer token for authentication
   * @returns {Promise<Object>} The chart spec object (chart_type, data, title?, options?)
   * @throws {Error} If the fetch fails or the chart is not found
   */
  static async fetchChartSpec(chartId, token) {
    const response = await fetch(`${this.API_BASE}/charts/${encodeURIComponent(chartId)}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch chart ${chartId}: ${response.status}`);
    }
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error(`Unexpected content-type from chart API: ${contentType}`);
    }
    const result = await response.json();
    return result.chart_spec;
  }

  static async fetchLatestChartSpecForConversation(conversationId, token) {
    const response = await fetch(
      `${this.API_BASE}/charts/conversations/${encodeURIComponent(conversationId)}/latest`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error(`Failed to fetch chart for conversation ${conversationId}: ${response.status}`);
    }
    const result = await response.json();
    return result.chart_spec ? { ...result.chart_spec, chart_id: result.chart_id } : null;
  }
}

export default ChartService;
